import type { TFunction } from 'i18next';
import type { MenuConfig } from '../lib/components';
import {
  ChartDiagramIcon,
  SettingsIcon,
  BenefitSetIcon,
  CreditcardIcon,
  CustomBenefitHeartIcon,
  CashBanknotePlusIcon
} from '../lib/icons';
import type { BookedBenefitsEnum, PricingModelEnum } from '@pxtra-client-lib-legacy/gen';
import {
  checkHasCompanyBenefitSets,
  checkHasCompanyCustomBenefits
} from '../lib/util/pricingModel';
import type { CardOnboardingResponse } from '../queries/pxtraCard';
import {
  hasOpenTaxDeadline,
  needsCompanyAction
} from '../lib/components/compound/pxtraCardOnboarding/sentence';

export const benefitsSubmenuConfig = (
  t: TFunction,
  pricingModel?: PricingModelEnum,
  bookedBenefits?: BookedBenefitsEnum[] | null,
  openCardApprovals = 0,
  onboarding?: CardOnboardingResponse,
  isCardEnabled = false,
  currentMonthShortfallCents = 0
): MenuConfig => {
  const isVerified = onboarding?.phase === 'verified';

  const walletEntry = {
    title: t('pxtraCard:walletMenu'),
    link: `/benefits/pxtra-card/wallet`,
    isActive: ({ pathname }: { pathname: string }) =>
      /\/benefits\/pxtra-card\/wallet/.test(pathname),
    hasTodo: currentMonthShortfallCents > 0
  };

  // While the check runs the setup is the only thing anyone can act on, so it
  // leads; afterwards it is ordinary settings and belongs at the end.
  const setupEntry = {
    title: isVerified ? t('pxtraCard:settings') : t('pxtraCard:setup'),
    link: `/benefits/pxtra-card/settings`,
    isActive: ({ pathname }: { pathname: string }) =>
      /\/benefits\/pxtra-card\/settings/.test(pathname),
    hasTodo: needsCompanyAction(onboarding) || hasOpenTaxDeadline(onboarding)
  };

  const cardsEntry = {
    title: t('pxtraCard:cards'),
    link: `/benefits/pxtra-card/cards`,
    isActive: ({ pathname }: { pathname: string }) => /\/benefits\/pxtra-card\/cards/.test(pathname)
  };

  const approvalsEntry = {
    title: openCardApprovals
      ? `${t('pxtraCard:approvals')} (${openCardApprovals})`
      : t('pxtraCard:approvals'),
    link: `/benefits/pxtra-card/approvals`,
    isActive: ({ pathname }: { pathname: string }) =>
      /\/benefits\/pxtra-card\/approvals/.test(pathname),
    hasTodo: openCardApprovals > 0
  };

  return [
    {
      title: t('common:PORTFOLIO'),
      isActive: ({ pathname }: { pathname: string }) => /\/benefits\/portfolio/.test(pathname),
      icon: ChartDiagramIcon,
      link: `/benefits/portfolio`
    },
    {
      title: t('common:CUSTOM_BENEFITS'),
      isActive: ({ pathname }) => /\/custom/.test(pathname),
      icon: CustomBenefitHeartIcon,
      link: `/benefits/custom`,
      hidden: !checkHasCompanyCustomBenefits(pricingModel)
    },
    {
      title: t('benefitSet:benefitSet_other'),
      isActive: ({ pathname }: { pathname: string }) => /\/benefit-set/.test(pathname),
      icon: BenefitSetIcon,
      link: `/benefits/benefit-set`,
      hidden: !checkHasCompanyBenefitSets(pricingModel, bookedBenefits)
    },
    {
      title: t('common:PXTRA_CARD'),
      isActive: ({ pathname }: { pathname: string }) => /\/pxtra-card/.test(pathname),
      icon: CreditcardIcon,
      link: `/benefits/pxtra-card`,
      hidden: !isCardEnabled,
      // The card pages stay reachable while the check runs: the status notice
      // belongs exactly where card functions are expected. The wallet exists
      // only once the account does, and then it leads.
      config: isVerified
        ? [walletEntry, cardsEntry, approvalsEntry, setupEntry]
        : [setupEntry, cardsEntry, approvalsEntry]
    },
    {
      title: t('company:DEFAULT_VALUES'),
      link: `/benefits/default-values`,
      icon: CashBanknotePlusIcon,
      isActive: ({ pathname }: { pathname: string }) => /\/default-values/.test(pathname)
    },
    {
      title: t('common:SETTINGS'),
      isActive: ({ pathname }: { pathname: string }) => /\/benefits\/settings/.test(pathname),
      icon: SettingsIcon,
      link: `/benefits/settings`,
      config: [
        {
          title: t('common:PAYROLL_EXPORT'),
          link: `/benefits/settings/payroll`,
          isActive: ({ pathname }: { pathname: string }) =>
            /\/benefits\/settings\/payroll/.test(pathname)
        }
      ]
    }
  ];
};
